import OneSignal from 'onesignal-cordova-plugin';

import { inject, Injectable, NgZone } from '@angular/core';
import { AlertController, NavController, Platform } from '@ionic/angular/standalone';

import { ApiService } from './api.service';
import { AuthService } from './auth.service';
import { DataService } from './data.service';

@Injectable({
  providedIn: 'root',
})
export class NotificationService {
  private auth = inject(AuthService);
  private platform = inject(Platform);
  private alertCtrl = inject(AlertController);
  private apiService = inject(ApiService);
  private dataService = inject(DataService);
  private zone = inject(NgZone);

  #navCtrl: NavController = inject(NavController);
  #initialized = false;
  #userAttached = false;

  async initOneSignal(): Promise<void> {
    if (this.#initialized) return;

    const platformReady = await this.platform.ready();
    if (!platformReady || !this.platform.is('hybrid')) return;

    this.#initialized = true;

    // Uncomment to set OneSignal device logging to VERBOSE
    // OneSignal.Debug.setLogLevel(6);

    OneSignal.initialize('b03ca7d4-f532-4d63-997a-1c3f78ca8156');
    OneSignal.Notifications.addEventListener('click', async (e) => {
      const clickData = e.notification;
      console.log('Notification Clicked : ' + JSON.stringify(clickData));

      const additionalData = clickData.additionalData as Record<string, string> | undefined;

      // Mark notification as read if we have the notification_id and the user is authenticated.
      // Skipping when unauthenticated is intentional — the destination route guards will redirect to login.
      if (additionalData?.['notification_id']) {
        const user = await this.auth.getUser();
        if (user) {
          this.apiService.putData('endpoint', { id: additionalData['notification_id'], read_at: 'now' });
        }
      }

      this.zone.run(() => {
        switch (additionalData?.['type']) {
          case 'ticket_message':
            this.#navCtrl.navigateRoot(['/tecservice-transportec/wallet/tickets/' + additionalData['ticket_id']]);
            break;
          case 'payment_success':
          case 'ticket_invoice':
            this.#navCtrl.navigateRoot(['/tecservice-transportec/wallet/transactions']);
            break;
          case 'busticket_reservation':
          case 'busticket_reminder':
          case 'busticket_scanned':
            if (additionalData['busticket_id']) {
              this.#navCtrl.navigateRoot(['/tecservice-transportec/tickets/ticket/' + additionalData['busticket_id']]);
            } else {
              this.#navCtrl.navigateRoot(['/tecservice-transportec/tickets']);
            }
            break;
          case 'busticket_availability':
            if (
              additionalData['realtrip_id'] &&
              additionalData['stop_id'] &&
              additionalData['date'] &&
              additionalData['route_id'] &&
              additionalData['radial_id']
            ) {
              this.dataService.setData('ticket-search', {
                stop: {
                  id: Number(additionalData['stop_id']),
                  name: additionalData['stop_name'] ?? '',
                  route_id: Number(additionalData['route_id']),
                  radial_id: Number(additionalData['radial_id']),
                },
                date: additionalData['date'] + ' 23:01:00',
                type: additionalData['direction'] ?? 'go',
                highlightRealtripId: Number(additionalData['realtrip_id']),
              });
              this.#navCtrl.navigateRoot(['/tecservice-transportec/transportec/search']);
            } else {
              this.#navCtrl.navigateRoot(['/tecservice-transportec/transportec/tickets']);
            }
            break;
          default:
            this.#navCtrl.navigateRoot(['/tecservice-transportec/wallet/notifications']);
            break;
        }
      });
    });
  }

  async attachUser(user: { id: number | string; type?: string } | null): Promise<void> {
    if (!user) return;
    if (this.#userAttached) return;

    const platformReady = await this.platform.ready();
    if (!platformReady || !this.platform.is('hybrid')) return;

    // initOneSignal is idempotent; ensure the SDK is up before login/tags.
    await this.initOneSignal();

    this.#userAttached = true;

    const permission = await OneSignal.Notifications.requestPermission(true);
    if (!permission) return;

    OneSignal.login(user.id.toString());
    OneSignal.User.addTags({
      id: user.id,
      type: user.type,
    });
  }

  async showAlert(title: string = 'Notificación', msg: string = '', task: string = 'OK') {
    const alert = await this.alertCtrl.create({
      header: title,
      subHeader: msg,
      buttons: [
        {
          text: `${task}`,
          handler: () => {
            // E.g: Navigate to a specific screen
          },
        },
      ],
    });
    alert.present();
  }
}
